/**
 * Pure helpers for the economy UI (no DOM, no engine): which services exist at which place, what a
 * buyer will take, how old a rumour is, how to word a job. Everything here is presentation: prices and
 * rules come from the server (or from the shared catalogue the server also uses).
 */
import { ALL_SUPPLIERS, BANKS, BUYERS, BUYER_TYPES, DISTRICT_IDS, GARAGE, LOCKUPS, RESPRAY, RUMOUR_POI_KINDS, RUMOUR_REACH, SHOP, depositFee, type BuyerDef, type LockupDef, type Place, type SupplierDef } from '../../shared/economy';
import { BIG_BUYERS, TEMP_SPOTS, type BigBuyerDef, type TempSpotDef } from '../../shared/markets';
import { getItemDef } from '../../shared/items';
import type { ItemView, JobOfferView, JobView, RumourBoard } from '../../shared/messages/economy';

export type HubTab = 'jobs' | 'bank' | 'supplier' | 'buyer' | 'market' | 'prices' | 'garage' | 'respray' | 'shop' | 'lockup' | 'operation';

export interface PoiLike {
  id: string;
  kind: string;
  name: string;
  pos: { x: number; z: number };
  radius: number;
}

/** Everything one point of interest offers. One panel ("hub") per POI instead of one prompt per service. */
export interface PlaceCluster {
  poi: string;
  name: string;
  pos: { x: number; z: number };
  /** How close the prompt appears (POI trigger radius + the widest service reach). */
  radius: number;
  banks: Place[];
  suppliers: SupplierDef[];
  buyers: BuyerDef[];
  garage: Place | null;
  respray: Place | null;
  shop: Place | null;
  lockups: LockupDef[];
  /** The district's big buyer, when he does business at this door (P3, docs/MARKETS.md). */
  big: BigBuyerDef[];
  /** A spot where temporary buyers turn up (only open while one is in town). */
  spots: TempSpotDef[];
  /** Pubs, the club, phone boxes and the safehouse: prices are overheard here. */
  rumours: boolean;
  /** Where the job board is within reach (phone boxes, pubs, the garage). */
  jobs: boolean;
}

const JOB_BOARD_KINDS: readonly string[] = ['phonebox', 'pub', 'garage'];

export function buildClusters(pois: readonly PoiLike[]): PlaceCluster[] {
  const byId = new Map<string, PlaceCluster>();
  const get = (p: PoiLike): PlaceCluster => {
    let c = byId.get(p.id);
    if (!c) {
      c = { poi: p.id, name: p.name, pos: { x: p.pos.x, z: p.pos.z }, radius: Math.max(3.5, p.radius), banks: [], suppliers: [], buyers: [], garage: null, respray: null, shop: null, lockups: [], big: [], spots: [], rumours: false, jobs: false };
      byId.set(p.id, c);
    }
    return c;
  };
  const poi = (id: string): PoiLike | undefined => pois.find((p) => p.id === id);
  const widen = (c: PlaceCluster, p: PoiLike, reach: number): void => {
    c.radius = Math.max(c.radius, p.radius + Math.min(reach, 8));
  };
  for (const b of BANKS) {
    const p = poi(b.poi);
    if (p) {
      const c = get(p);
      c.banks.push(b);
      widen(c, p, b.reach);
    }
  }
  for (const s of ALL_SUPPLIERS) {
    const p = poi(s.poi);
    if (p) {
      const c = get(p);
      c.suppliers.push(s);
      widen(c, p, s.reach);
    }
  }
  for (const b of BUYERS) {
    const p = poi(b.poi);
    if (p) {
      const c = get(p);
      c.buyers.push(b);
      widen(c, p, b.reach);
    }
  }
  for (const [place, key] of [
    [GARAGE, 'garage'],
    [RESPRAY, 'respray'],
    [SHOP, 'shop'],
  ] as const) {
    const p = poi(place.poi);
    if (p) {
      const c = get(p);
      c[key] = place;
      widen(c, p, place.reach);
    }
  }
  for (const l of LOCKUPS) {
    const p = poi(l.poi);
    if (p) {
      const c = get(p);
      c.lockups.push(l);
      widen(c, p, l.reach);
    }
  }
  for (const [list, key] of [
    [BIG_BUYERS, 'big'],
    [TEMP_SPOTS, 'spots'],
  ] as const) {
    for (const b of list) {
      const p = poi(b.poi);
      if (!p) continue;
      const c = get(p);
      (c[key] as Place[]).push(b);
      widen(c, p, b.reach);
    }
  }
  for (const p of pois) {
    if ((RUMOUR_POI_KINDS as readonly string[]).includes(p.kind)) {
      const c = get(p);
      c.rumours = true;
      widen(c, p, RUMOUR_REACH);
    }
    if (JOB_BOARD_KINDS.includes(p.kind)) get(p).jobs = true;
  }
  return [...byId.values()];
}

/** Tabs a cluster offers, in display order. */
export function clusterTabs(c: PlaceCluster): HubTab[] {
  const t: HubTab[] = [];
  if (c.jobs) t.push('jobs');
  if (c.banks.length) t.push('bank');
  if (c.suppliers.length) t.push('supplier');
  if (c.buyers.length) t.push('buyer');
  if (c.big.length || c.spots.length) t.push('market');
  if (c.rumours) t.push('prices');
  if (c.garage) t.push('garage');
  if (c.respray) t.push('respray');
  if (c.shop) t.push('shop');
  if (c.lockups.length) t.push('lockup', 'operation');
  return t;
}

export const TAB_LABEL: Record<HubTab, string> = {
  jobs: 'Jobs',
  bank: 'Bank',
  supplier: 'Buy',
  buyer: 'Sell',
  market: 'Big buyer',
  prices: 'Word on the street',
  garage: 'Motors',
  respray: 'Respray',
  shop: 'Back room',
  lockup: 'Lockup',
  operation: 'Operation',
};

/** The premises tab is named after the kind of place (the railway lockups keep "Lockup"). */
export function tabLabel(c: PlaceCluster, t: HubTab): string {
  if (t === 'lockup' && c.lockups[0] && c.lockups[0].site !== 'lockup') return 'Premises';
  if (t === 'lockup' && c.lockups[0]?.acquire === 'take') return 'Premises';
  if (t === 'market' && !c.big.length) return 'The buyer';
  return TAB_LABEL[t];
}

/** What the E prompt says at a cluster. */
export function clusterPrompt(c: PlaceCluster): string {
  const bank = c.banks[0];
  if (c.garage) return `${c.name} — motors, repairs, jobs`;
  if (c.respray) return `${c.name} — respray shop`;
  if (c.lockups.length) return `${c.lockups[0]!.name} — ${c.lockups[0]!.site === 'lockup' && c.lockups[0]!.acquire === 'buy' ? 'storage' : 'premises'}`;
  if (c.suppliers.length) return `${c.name} — buy goods`;
  if (c.buyers.length) return `${c.name} — sell goods`;
  if (c.shop) return `${c.shop.name}`;
  if (c.big.length && !c.rumours && !c.jobs) return `${c.name} — big buyer`;
  if (c.spots.length && !c.rumours && !c.jobs && !bank) return `${c.spots[0]!.name} — a buyer from out of town`;
  // A pub's or a phone box's cashpoint is a side counter: name the place and its board first.
  if (bank) return bank.kind === 'bank' ? `${bank.name}` : c.jobs ? `${c.name} — jobs, cashpoint` : `${bank.name} — cashpoint`;
  if (c.jobs) return `${c.name} — jobs and prices`;
  return `${c.name} — prices (word on the street)`;
}

/** The red line above a hub's title: the kind of place, not its first tab (a pub is "Pub", not "Cashpoint"). */
export function clusterKicker(c: PlaceCluster, kind?: string): string {
  // The kind of place, never the title again (HALLORAN MOTORS over HALLORAN MOTORS) nor a side tab's
  // kind (Pike's was "The big buyer" because his yard also hosts one).
  if (c.garage) return 'The garage';
  if (c.respray) return 'The paint shop';
  if (c.lockups.length) return c.lockups[0]!.site === 'warehouse' ? 'Warehouse' : c.lockups[0]!.site === 'rural' ? 'Out of town' : 'Lockup';
  if (c.suppliers.length) return 'Buy goods here';
  if (c.buyers.length) return c.buyers[0]!.type === 'fence' ? 'Fence · buys stolen goods' : 'Street dealer · buys goods';
  if (c.big.length && !c.banks.length && !c.jobs) return 'The big buyer';
  if (c.spots.length && !c.banks.length && !c.jobs) return 'In town for one night';
  const place = kind ? PLACE_KIND[kind] : undefined;
  if (place) return place;
  if (c.banks.length) return c.banks[0]!.kind === 'bank' ? 'The bank' : 'Cashpoint';
  return c.jobs ? 'Jobs board' : 'Prices';
}

const PLACE_KIND: Record<string, string> = {
  pub: 'Pub',
  club: 'Club',
  phonebox: 'Phone box',
  safehouse: 'Safehouse',
  bank: 'The bank',
  policeStation: 'The nick',
};

/** Period names for the forecourt's paints (every colour in the vehicle roster; a stray hex gets a plain name). */
const PAINT_NAMES: Record<number, string> = {
  0x9b2d20: 'Oxblood',
  0x2f4f3a: 'Bottle green',
  0xd8c690: 'Primrose',
  0x2b3b5c: 'Navy',
  0xe4e0d4: 'Old English white',
  0x6b4a2f: 'Tobacco brown',
  0xc77f2a: 'Amber',
  0x1b1d22: 'Soot black',
  0x5a1c1e: 'Damson',
  0x334a5e: 'Slate blue',
  0x8c8c86: 'Gunmetal',
  0x3c3226: 'Walnut',
  0xbfb59a: 'Fawn',
  0x2d4033: 'Racing green',
  0xe8e4d6: 'Ermine white',
  0x1f3a5f: 'Midnight blue',
  0x7b2d1d: 'Brick red',
  0x4b5a3a: 'Olive drab',
  0xc9a227: 'Mustard',
  0xb81d13: 'Pillar-box red',
  0xf2c14e: 'Saffron',
  0x0f5257: 'Teal',
  0xeeeeee: 'White',
  0x111111: 'Jet black',
  0x3d6b3a: 'Leaf green',
  0xeeeeea: 'Police white',
  0x141414: 'Jet black',
  0x3b2a4a: 'Aubergine',
  0x8a3b22: 'Rust',
  0x2e4a6b: 'Steel blue',
  0xb08a2e: 'Harvest gold',
  0x43513a: 'Moss',
};

export function paintName(hex: number): string {
  const n = PAINT_NAMES[hex];
  if (n) return n;
  // Nearest of the named ones, so a new roster colour still gets a word rather than a code.
  let best = 'Its own colour';
  let bd = Infinity;
  for (const [k, name] of Object.entries(PAINT_NAMES)) {
    const h = Number(k);
    const d = Math.abs((h >> 16) - (hex >> 16)) + Math.abs(((h >> 8) & 255) - ((hex >> 8) & 255)) + Math.abs((h & 255) - (hex & 255));
    if (d < bd) {
      bd = d;
      best = name;
    }
  }
  return best;
}

// ----------------------------------------------------------------------------- goods

export function itemName(def: string, qty = 1): string {
  const d = getItemDef(def);
  if (!d) return def;
  return qty === 1 ? d.name : d.plural;
}

/** A good without its packaging, for narrow columns: "stolen fur coats", "smuggled silk". */
export function shortGood(def: string): string {
  return itemName(def, 2).replace(/^(crates|cases|bolts|cartons|bags|bundles|boxed) of /, '').replace(/^boxed /, '');
}

/** "Street dealer" / "Fence" — one name per kind of buyer, everywhere. */
export function buyerKind(type: 'street' | 'fence'): string {
  return type === 'fence' ? 'Fence' : 'Street dealer';
}

/** Stacks a buyer will take (right goods; equipment never sells). */
export function sellableStacks(buyer: BuyerDef, items: readonly ItemView[]): ItemView[] {
  return items.filter((i) => buyer.accepts.includes(i.def) && i.qty > 0 && getItemDef(i.def)?.category !== 'equipment');
}

export function maxSaleQty(buyer: BuyerDef, stack: ItemView): number {
  return Math.max(1, Math.min(stack.qty, BUYER_TYPES[buyer.type].maxQty));
}

/** First-unit price a rumour board quoted for (buyer, good), or null. */
export function boardPrice(board: Pick<RumourBoard, 'prices'> | null, buyerId: string, def: string): number | null {
  const p = board?.prices.find((x) => x.buyer === buyerId && x.def === def);
  return p ? p.unit : null;
}

export function ageText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 10) return 'just now';
  if (s < 90) return `${s}s ago`;
  return `${Math.round(s / 60)} min ago`;
}

/** Police attention (0..1) in plain words; with a scanner the server sends exact values, otherwise rounded. */
export function attentionLabel(a: number): 'quiet' | 'watchful' | 'heavy' {
  return a < 0.34 ? 'quiet' : a < 0.67 ? 'watchful' : 'heavy';
}

/** What a district's police level means for a seller, in a few words. */
export function attentionMeaning(a: number): string {
  const l = attentionLabel(a);
  return l === 'quiet' ? 'full prices, few stops' : l === 'watchful' ? 'prices a bit lower, some stops' : 'prices lower, many stops';
}

export function districtName(id: string): string {
  return (
    {
      'cinder-yards': 'Cinder Yards',
      'the-parade': 'The Parade',
      'tanners-row': "Tanner's Row",
      'mercy-square': 'Mercy Square',
      'gullet-basin': 'Gullet Basin',
      'kiln-common': 'Kiln Common',
    } as Record<string, string>
  )[id] ?? id;
}

export const DISTRICT_ORDER: readonly string[] = DISTRICT_IDS;

/** Fee preview for a deposit at a bank / cashpoint (the server charges the same maths). */
export function depositPreview(kind: 'bank' | 'atm', amount: number): { fee: number; credited: number } {
  const a = Math.max(0, Math.floor(amount));
  const fee = a > 0 ? depositFee(kind, a) : 0;
  return { fee, credited: Math.max(0, a - fee) };
}

// ----------------------------------------------------------------------------- jobs

export function jobTitle(kind: 'courier' | 'recovery'): string {
  return kind === 'courier' ? 'Courier run' : 'Vehicle recovery';
}

export function offerLine(o: Pick<JobOfferView, 'kind' | 'distance'>): string {
  return o.kind === 'courier' ? `Parcel across town · ${Math.round(o.distance)} m` : `Bring a motor in to Halloran's · ${Math.round(o.distance)} m`;
}

/** Seconds left on a job given the server deadline (server ms) and our estimate of the server clock. */
export function secondsLeft(deadline: number, serverNow: number): number {
  return Math.max(0, Math.ceil((deadline - serverNow) / 1000));
}

export function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The job step the player should see on the objective sheet. */
/**
 * The board's recovery offers ("Phone box, Bell Street → Halloran Motors"): the car waits by the first place.
 * The clipping used to send you straight to the second, with no car — a first job that went nowhere.
 */
export const recoveryPickup: { runId: string; from: string } = { runId: '', from: '' };

/**
 * `fetch`: a recovery job's car — whether you're at its wheel, and the place it was left by (when known).
 * Without it a recovery job reads as the hand-over only.
 */
export function jobInstruction(job: JobView, poiName: (id: string) => string, fetch?: { atWheel: boolean; from?: string }): { text: string; hint: string } {
  if (job.stage === 'pickup' && job.pickup) {
    return { text: `Collect the parcel at ${poiName(job.pickup.poi)}`, hint: 'Get close and press the key.' };
  }
  if (job.kind === 'recovery' && fetch && !fetch.atWheel) {
    return {
      text: fetch.from ? `Fetch the grey motor from ${poiName(fetch.from)}` : 'Fetch the grey motor — follow the red marker',
      hint: `Then drive it in to ${poiName(job.dropoff.poi)}. Dents cost you.`,
    };
  }
  if (job.kind === 'recovery') return { text: `Bring the motor in to ${poiName(job.dropoff.poi)}`, hint: 'Drive it there in one piece — dents cost you.' };
  return { text: `Deliver to ${poiName(job.dropoff.poi)}`, hint: 'Too fast to be true gets refused. Drive it.' };
}
